import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { billingApi } from "@/lib/account";

/**
 * Three tier cards. The Pro/Enterprise CTAs only light up if billing is
 * enabled on this deployment (STRIPE_ENABLED=true server-side). Until then
 * we say "Coming soon" so the page is presentable now and flips on without
 * a redeploy when prices land.
 */

const TIERS = [
  {
    id: "free",
    name: "Free",
    headline: "For everyone, today.",
    price: "$0",
    cadence: "Free during launch — no card required.",
    features: [
      "Chat with Humane Behavior AI",
      "Selective Memory (opt-in)",
      "Sign in with Google or email",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    headline: "For builders and analysts.",
    price: null, // resolved at runtime if Stripe is enabled
    cadence: "Monthly billing via Stripe.",
    features: [
      "Programmatic API access (60 req/min)",
      "1M requests per month per key",
      "Priority during peak load",
      "Side-by-side eval harness",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    headline: "For teams and platforms.",
    price: null,
    cadence: "Monthly billing via Stripe. Talk to us for volume rates.",
    features: [
      "Programmatic API access (240+ req/min)",
      "Unlimited monthly requests",
      "Custom fine-tuning slots",
      "SLA + dedicated support channel",
    ],
  },
];

export default function Billing() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [busyTier, setBusyTier] = useState(null);
  const [pollStatus, setPollStatus] = useState(null); // {state, message}

  const refreshConfig = useCallback(() => {
    billingApi
      .config()
      .then(setConfig)
      .catch((e) => toast.error(e.message || "Failed to load billing"));
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") refreshConfig();
  }, [user, refreshConfig]);

  // Poll status if redirected back from Stripe with ?session_id=
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !user || typeof user !== "object") return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const status = await billingApi.status(sid);
        if (status.payment_status === "paid") {
          setPollStatus({ state: "ok", message: `Payment confirmed — you are now on ${status.tier}.` });
          toast.success("Payment confirmed.");
          refreshConfig();
          return;
        }
        if (status.status === "expired") {
          setPollStatus({ state: "err", message: "Payment session expired — try again." });
          return;
        }
        if (attempts >= maxAttempts) {
          setPollStatus({ state: "pending", message: "Still processing. Refresh in a minute or check your email." });
          return;
        }
        setPollStatus({ state: "pending", message: "Verifying payment…" });
        setTimeout(poll, 2000);
      } catch (e) {
        setPollStatus({ state: "err", message: e.message || "Status check failed" });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [params, user, refreshConfig]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/account/billing" }} replace />;

  const cancelled = params.get("cancelled");
  const stripeOn = !!config?.enabled;
  const tiersConfigured = new Set(config?.tiers_configured || []);
  const currentTier = config?.current_tier || "free";

  const onUpgrade = async (tier) => {
    if (!stripeOn || !tiersConfigured.has(tier)) {
      toast.info("Coming soon — billing is not enabled on this deployment yet.");
      return;
    }
    setBusyTier(tier);
    try {
      const res = await billingApi.checkout({
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.message || "Checkout failed");
      setBusyTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="billing-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back to chat
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          Billing
        </h1>
        {user?.api_keys_enabled ? (
          <Link
            to="/account/api-keys"
            data-testid="billing-to-keys"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
          >
            API keys →
          </Link>
        ) : (
          <span className="w-16" />
        )}
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        <section className="py-14">
          <Sparkles size={28} className="text-[var(--accent)] mb-5" />
          <h2 className="font-serif text-4xl sm:text-5xl text-[var(--text)] leading-[1.1] max-w-3xl mb-5">
            Free while we grow. Paid tiers arrive when you do.
          </h2>
          <p className="font-sans text-base text-[var(--text-muted)] max-w-2xl leading-relaxed">
            Everything in Humane Behavior AI is free during launch. Paid tiers
            below are wired into Stripe — they activate the day we flip the
            switch. You currently sit on the <span className="text-[var(--accent)]">{currentTier}</span> tier.
          </p>
        </section>

        {pollStatus && (
          <div
            data-testid="poll-status"
            className={`border px-5 py-4 mb-8 font-mono-ui text-[11px] tracking-[0.22em] uppercase ${
              pollStatus.state === "ok"
                ? "border-[var(--accent)] text-[var(--accent)]"
                : pollStatus.state === "err"
                ? "border-red-500 text-red-400"
                : "border-[var(--border)] text-[var(--text-muted)]"
            }`}
          >
            {pollStatus.message}
          </div>
        )}

        {cancelled && (
          <div
            data-testid="cancelled-banner"
            className="border border-[var(--border)] px-5 py-4 mb-8 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-muted)]"
          >
            Checkout cancelled — no changes were made.
          </div>
        )}

        <section className="grid md:grid-cols-3 gap-4">
          {TIERS.map((t) => {
            const isCurrent = currentTier === t.id;
            const ctaEnabled = t.id !== "free" && stripeOn && tiersConfigured.has(t.id);
            return (
              <div
                key={t.id}
                data-testid={`tier-card-${t.id}`}
                className={`border p-7 flex flex-col ${
                  isCurrent ? "border-[var(--accent)]" : "border-[var(--border)]"
                }`}
              >
                <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-3">
                  {t.name}
                </div>
                <div className="font-serif text-2xl text-[var(--text)] leading-tight mb-4">
                  {t.headline}
                </div>
                <div className="font-serif text-4xl text-[var(--text)] mb-2">
                  {t.id === "free" ? t.price : (ctaEnabled ? "Live" : "Coming soon")}
                </div>
                <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-6">
                  {t.cadence}
                </div>
                <ul className="space-y-2.5 mb-7 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 font-sans text-sm text-[var(--text-muted)]">
                      <Check size={14} className="text-[var(--accent)] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {t.id === "free" ? (
                  <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] py-3 text-center border border-[var(--border-soft)]">
                    {isCurrent ? "Current tier" : "Default tier"}
                  </div>
                ) : (
                  <button
                    onClick={() => onUpgrade(t.id)}
                    disabled={busyTier === t.id || isCurrent}
                    data-testid={`upgrade-${t.id}`}
                    className={`font-mono-ui text-[10px] tracking-[0.28em] uppercase py-3 border ${
                      ctaEnabled && !isCurrent
                        ? "border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)]"
                        : "border-[var(--border)] text-[var(--text-dim)]"
                    } disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2`}
                  >
                    {busyTier === t.id && <Loader2 className="animate-spin" size={12} />}
                    {isCurrent
                      ? "You're on this tier"
                      : busyTier === t.id
                      ? "Redirecting…"
                      : ctaEnabled
                      ? `Upgrade to ${t.name}`
                      : "Coming soon"}
                  </button>
                )}
              </div>
            );
          })}
        </section>

        <section className="py-14 mt-10 border-t border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-3">
            How billing works
          </div>
          <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed max-w-2xl">
            Stripe Hosted Checkout. Webhook updates your tier in real time.
            Cancel any time — your tier remains active until the end of the
            paid period. Refunds processed within 7 days for unhappy weeks.
          </p>
        </section>
      </main>
    </div>
  );
}
